import {
  type ConstantMap,
  type Nil,
  type UnitsState,
  UnitUtils,
  Utils,
} from '@pbt/pbt-ui-components'
import { UnitTypes } from '@pbt/pbt-ui-components/src/localization'

import type {
  SnapshotVitalBigDecimalValuePoint,
  SnapshotVitalConstantValuePoint,
  SnapshotVitalIntValuePoint,
  SnapshotVitalStringValuePoint,
  Subscription,
} from '~/api/graphql/generated/types'
import { vitalsTableConfig } from '~/components/dashboard/soapV2/vitals/config/vitalsTableConfig'
import config from '~/components/dashboard/soapV2/vitals/config/vitalsTableNoPatientSharingConfig'
import { getVitalLabel } from '~/utils/vitalUtils'

export type VitalEntry =
  | SnapshotVitalBigDecimalValuePoint
  | SnapshotVitalConstantValuePoint
  | SnapshotVitalIntValuePoint
  | SnapshotVitalStringValuePoint

export interface TransformedVital {
  creationDate: string
  notes: string
  value?: string | number | boolean
}

export interface TransformationContext {
  constantMap: ConstantMap
  isPatientSharingEnabled: boolean
  unitsState: UnitsState
}

export enum VitalTransformationType {
  RAW = 'raw',
  TEMPERATURE = 'temperature',
  LABELED = 'labeled',
}

const isConstantEntity = (value: VitalEntry['value']) =>
  value && typeof value === 'object' && 'id' in value && 'name' in value

// Helper function to extract value from GraphQL response
export const extractValue = (value: VitalEntry['value']): string => {
  if (!value) return ''

  // Handle object values (ConstantEntity with id and name)
  if (isConstantEntity(value)) {
    return String(value.name || value.id || '')
  }

  // Handle simple values (string, number, boolean)
  return String(value)
}

// Create a vital transformer function based on transformation type
export const createVitalTransformer =
  (
    vitalKey: string,
    transformationType: VitalTransformationType,
    context: TransformationContext,
  ) =>
  (vital: VitalEntry): TransformedVital => {
    const rawValue = extractValue(vital.value)
    let processedValue: string

    switch (transformationType) {
      case VitalTransformationType.TEMPERATURE:
        const convertedValue = UnitUtils.convertUnits(
          UnitTypes.TEMPERATURE,
          Number(rawValue),
          context.unitsState,
        )
        processedValue = String(convertedValue)
        break
      case VitalTransformationType.LABELED:
        const vitalConfigItem = Utils.findById(
          vitalKey,
          context.isPatientSharingEnabled ? vitalsTableConfig : config,
        )
        processedValue = getVitalLabel(
          rawValue,
          vitalConfigItem,
          context.constantMap,
        )
        break
      default: // 'raw'
        processedValue = rawValue
    }

    return {
      value: processedValue,
      creationDate: vital.creationDate,
      notes: vital.notes || '',
    }
  }

// Mapping configuration for each vital type
export const VITAL_TRANSFORM_MAP: Record<string, VitalTransformationType> = {
  weight: VitalTransformationType.RAW,
  temperature: VitalTransformationType.TEMPERATURE,
  heartRate: VitalTransformationType.LABELED,
  respirationRate: VitalTransformationType.LABELED,
  capillaryRefillTime: VitalTransformationType.LABELED,
  mucousMembraneColorId: VitalTransformationType.LABELED,
  mucousMembraneMoistureId: VitalTransformationType.LABELED,
  skinTurgorId: VitalTransformationType.LABELED,
  bodyConditionScore: VitalTransformationType.LABELED,
  fearAnxietyScore: VitalTransformationType.LABELED,
  painScore: VitalTransformationType.LABELED,
  dyspnea: VitalTransformationType.LABELED,
  mentationId: VitalTransformationType.LABELED,
  pulseCharacterId: VitalTransformationType.LABELED,
  bloodPressure: VitalTransformationType.LABELED,
  spo2: VitalTransformationType.LABELED,
  etco2: VitalTransformationType.LABELED,
  o2Level: VitalTransformationType.LABELED,
  isoSevoLevel: VitalTransformationType.LABELED,
  recumbencyId: VitalTransformationType.LABELED,
  muscleMassScore: VitalTransformationType.LABELED,
}

// Transform all vitals data using the mapping configuration
export const transformVitalsData = (
  vitalsData: Subscription['vitalsWidget'] | Nil,
  context: TransformationContext,
): Record<string, TransformedVital[]> => {
  if (!vitalsData) return {}

  return Object.keys(VITAL_TRANSFORM_MAP).reduce(
    (acc, vitalKey) => {
      const transformationType = VITAL_TRANSFORM_MAP[vitalKey]

      const transformer = createVitalTransformer(
        vitalKey,
        transformationType,
        context,
      )
      acc[vitalKey] =
        (
          vitalsData[
            vitalKey as keyof Subscription['vitalsWidget']
          ] as Array<VitalEntry>
        )?.map(transformer) || []
      return acc
    },
    {} as Record<string, TransformedVital[]>,
  )
}
